import { Link, Navigate } from "react-router-dom";
import { termsReady } from "../lib/termsOfUse";
import { useAuth } from "../context/AuthContext";
import { CompassLogo } from "../components/CompassLogo";
import { Button } from "../components/ui/button";
import { ClipboardList, LineChart, UserPlus, MapPin, Target, TrendingUp } from "lucide-react";
import { homePathForRole } from "../lib/homePath";

// #186's brand pair. The app's interior pages still carry the pre-rebrand
// navy/gold (#0B2E59/#D4A017) — repainting them is #188's job, not this
// slice's — but the front door is the one surface where the brand guide's
// own colours have to be the ones a stranger sees, and it is already where
// the rebranded mark lives.
const NAVY = "#0D1B3E";
const GREEN = "#5D9D35";

// What the product does today, in the app's own terms. Every line here is
// something a visitor can go and find after signing in — no roadmap, no
// trial (the mechanics for #97's 21-day trial do not exist), no pricing.
const WHAT_IT_DOES = [
  {
    icon: ClipboardList,
    title: "Log the match while you remember it",
    body:
      "Date, competition, opponent, ground, and your innings — runs, balls, boundaries, how you got out, overs and wickets. " +
      "It is an innings-level record, entered in a minute on a phone at the boundary.",
  },
  {
    icon: LineChart,
    title: "Watch a season add up",
    body:
      "Averages, strike rate and form come out of the matches you have logged, filtered by season, competition or team. " +
      "A career record that starts at your first innings and keeps going.",
  },
  {
    icon: UserPlus,
    title: "A coach you invite — and can un-invite",
    body:
      "The player sends the invitation; a coach cannot find a player and attach themselves. " +
      "An invited coach sees real performances, leaves feedback against them and sets goals. " +
      "Withdraw the invitation and the access goes with it, the same minute.",
  },
  {
    icon: MapPin,
    title: "Training, grounds and guides",
    body:
      "Log training sessions and what you worked on. Grounds are pinned on a map, so Saturday's fixture has a location, not a name. " +
      "Reference guides for batting, bowling, fielding and fitness sit alongside them.",
  },
  {
    icon: Target,
    title: "Set a goal, and know when you've hit it",
    body:
      "A batting average, a strike rate, a target your coach set for you — pick it, set a date if you want one, " +
      "and it sits on your record from that point on. No spreadsheet to update, no number to remember. " +
      "The next matching innings you log moves you toward it automatically.",
  },
  {
    icon: TrendingUp,
    title: "Watch your own trend line",
    body:
      "This season against last, your last five innings against your first five, where you stood the day a goal " +
      "was set against where you stand now. Not a single stat, but the shape of it over time — the difference " +
      "between \"I think I'm improving\" and being able to point at the line that shows it.",
  },
];

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="max-w-2xl">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GREEN }}>
        {eyebrow}
      </p>
      <h2 className="font-display font-black text-2xl sm:text-3xl mt-2" style={{ color: NAVY }}>
        {title}
      </h2>
      {children && <p className="mt-3 text-slate-600">{children}</p>}
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();

  // `user` is null while AuthContext's /auth/me probe is in flight and false
  // once it comes back anonymous. We deliberately render the page during that
  // probe instead of holding a spinner: the overwhelmingly common visitor to
  // this route is a stranger following a link Martin sent, and making them
  // wait on an auth round-trip to read what the product is would defeat the
  // point of the page. A signed-in visitor gets a brief flash of it before the
  // redirect below fires — the right way round for this trade.
  // #384 item 3: the signed-in home is role-dependent — a coach's is My
  // Players. Sending everyone to /dashboard would still WORK (the router
  // bounces a coach on), but it would put a page they cannot see in their
  // history and make the front door flash a redirect chain.
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;

  return (
    <div className="min-h-screen bg-white" data-testid="landing">
      <header className="border-b border-slate-200">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="min-w-0">
            <CompassLogo size={44} />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link
              to="/login"
              className="text-sm font-semibold px-2 py-1"
              style={{ color: NAVY }}
              data-testid="landing-signin"
            >
              Sign in
            </Link>
            <Link to="/register">
              <Button className="h-10 px-4 font-semibold" style={{ background: NAVY }} data-testid="landing-signup">
                Create account
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 sm:px-8">
        {/* Hero */}
        <section className="py-12 sm:py-16">
          <h1
            className="font-display font-black text-3xl sm:text-5xl leading-tight"
            style={{ color: NAVY }}
            data-testid="landing-headline"
          >
            Nobody becomes a great player by accident. They become one on purpose, innings by innings.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Cricket has never lacked data. What it's lacked is direction. CricketCompass turns performance
            stats into a clear pathway forward — for players, coaches, and clubs.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button
                className="h-12 px-6 text-base font-semibold"
                style={{ background: GREEN }}
                data-testid="landing-hero-signup"
              >
                Create an account
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="outline"
                className="h-12 px-6 text-base font-semibold"
                data-testid="landing-hero-signin"
              >
                I already have one
              </Button>
            </Link>
          </div>
        </section>

        {/* What is this — Martin's own framing of the product, sitting directly
            under the hero CTA pair (#266). */}
        <section className="pb-12 sm:pb-16" data-testid="landing-whatis">
          <div className="rounded-xl border border-slate-200 p-6 sm:p-8 min-w-0">
            <h2 className="font-display font-black text-2xl sm:text-3xl" style={{ color: NAVY }}>
              What is this
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl">
              CricketCompass is an all-in-one cricket statistics and player development platform. We don't
              just track performance — we guide progress, so every player knows what's working, every coach
              can build on it, and every club can see the pathway they're developing.
            </p>
          </div>
        </section>

        {/* What it does */}
        <section className="pb-12 sm:pb-16" data-testid="landing-what">
          <SectionHeading eyebrow="What it does" title="The specifics, not the sales pitch">
            Six things, all of them working today. Nothing here is a promise about a later release.
          </SectionHeading>
          {/* min-w-0 on every grid item: a grid item defaults to
              min-width:auto and refuses to shrink below its content, which is
              exactly the latent defect #251 catalogues — the widest child sets
              the track, the track grows the grid, and phone-390 scrolls
              sideways. */}
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {WHAT_IT_DOES.map((item) => (
              <div
                key={item.title}
                className="min-w-0 rounded-xl border border-slate-200 p-5"
                data-testid="landing-feature"
              >
                <item.icon size={22} style={{ color: GREEN }} />
                <h3 className="font-semibold text-lg mt-3" style={{ color: NAVY }}>
                  {item.title}
                </h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Juniors and guardians — deliberately ABOVE the sign-up call to
            action. A parent deciding whether their child signs up should not
            have to register to find this out (#169, #228). */}
        <section className="pb-12 sm:pb-16" data-testid="landing-juniors">
          <div className="rounded-xl border-2 p-6 sm:p-8 min-w-0" style={{ borderColor: NAVY }}>
            <SectionHeading eyebrow="If the player is a junior" title="How a junior's account works" />
            {/* Every line below is true of the app as it stands today. It
                deliberately does NOT state the credential handover at 11 that
                #169 decides and nothing yet implements — a parent who read a
                date here would expect something to happen on a birthday, and
                nothing would. Same rule that keeps #97's trial off the front
                door: the front page says only what the app can honour. */}
            <ul className="mt-6 space-y-4 text-slate-700">
              <li className="min-w-0">
                <span className="font-semibold" style={{ color: NAVY }}>
                  The account is the player's, from the first day.
                </span>{" "}
                It is set up in their name and it stays theirs — one account and one record, building from
                the first innings on. There is never a second account.
              </li>
              <li className="min-w-0">
                <span className="font-semibold" style={{ color: NAVY }}>
                  While the player is under 11, you hold the sign-in details.
                </span>{" "}
                The parent or guardian's email address is the account's contact address, and the guardian
                sets and keeps the password, operating the account on the player's behalf.
              </li>
              <li className="min-w-0">
                <span className="font-semibold" style={{ color: NAVY }}>
                  A coach only ever sees a player who invited them.
                </span>{" "}
                There is no directory of children to browse, no way for a coach to attach themselves to a
                player, and the invitation can be withdrawn at any time.
              </li>
            </ul>
            <p className="mt-6 text-sm text-slate-500">
              This is why sign-up asks for the player's date of birth — it is what decides which of the
              above applies.
            </p>
          </div>
        </section>
      </main>

      <footer style={{ background: NAVY }}>
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-12 text-white">
          <h2 className="font-display font-black text-2xl sm:text-3xl max-w-2xl">
            Start the record with this weekend's game.
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button
                className="h-12 px-6 text-base font-semibold"
                style={{ background: GREEN }}
                data-testid="landing-footer-signup"
              >
                Create an account
              </Button>
            </Link>
            <Link
              to="/login"
              className="text-sm font-semibold text-white/80 hover:text-white px-2 py-1"
              data-testid="landing-footer-signin"
            >
              Already have an account? Sign in
            </Link>
          </div>
          {/* Issue #449 (interim, partial): the footer is where a visitor
              who never opens the sign-up form — or came back later with a
              question — can still find the privacy notice. Public route. */}
          <Link
            to="/privacy"
            className="mt-6 inline-block text-xs text-white/60 hover:text-white/90 underline"
            data-testid="landing-footer-privacy"
          >
            Privacy notice
          </Link>
          {/* Issue #1340 (D367.3): beside it, once the terms text exists. */}
          {termsReady() && (
            <Link
              to="/terms"
              className="mt-6 ml-4 inline-block text-xs text-white/60 hover:text-white/90 underline"
              data-testid="landing-footer-terms"
            >
              Terms of use
            </Link>
          )}
        </div>
      </footer>
    </div>
  );
}
